import { Combobox } from '@headlessui/react';
import type { Optional } from '@weft/utils';
import { isEmptyArray, isNullOrUndefined } from '@weft/utils';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ErrorLabel } from '../../../../components/design/ErrorLabel';
import { ArrowIcon } from '../../../../components/icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../../../components/icons/ArrowIcon/types/arrow-icon-direction';

export type PlanSettingsSelectItem<Item> = {
  label: string;
  value: Item;
};

export enum PlanSettingsSelectMode {
  Default = 'default',
  Search = 'search',
}

type PlanSettingsSelectProps<Item> = {
  items: PlanSettingsSelectItem<Item>[];
  onChange: (item: Item) => void;
  label: string;
  value: Item | undefined;
  disabled?: boolean;
  className?: string;
  errorMessage?: string;
  mode?: PlanSettingsSelectMode;
};

export const PlanSettingsSelect = <Item, _ = undefined>({
  items,
  onChange,
  label,
  value,
  disabled,
  className,
  errorMessage,
  mode = PlanSettingsSelectMode.Default,
}: PlanSettingsSelectProps<Item>) => {
  const [query, setQuery] = useState('');
  const filteredItems = mode === PlanSettingsSelectMode.Default ? items : items.filter(item => item.label.toLowerCase().includes(query.toLowerCase()));
  const selectedDisplayValue = getItemsValue(items, value);

  return (
    <div className="flex w-full flex-col gap-y-[7px]">
      <Combobox
        as="div"
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={twMerge('rounded-2lg border-line w-full relative h-16 border bg-white', className)}
      >
        <div className="relative">
          <Combobox.Button
            as="div"
            className="h-full w-full cursor-pointer bg-transparent"
          >
            <div className="flex items-start justify-between p-5">
              <span className="text-filled-text">{label}</span>
              <span className="flex items-center space-x-2.5">
                <span className='min-h-6 text-style max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-left'>
                  { mode === 'search' ? (
                    <Combobox.Input
                      className="w-full border-none bg-transparent focus:outline-none"
                      displayValue={(item: Item) => getItemsValue(items, item)?.label || ''}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search..."
                    />
                  ) : selectedDisplayValue?.label}
                </span>
               
                <ArrowIcon
                  className="text-style"
                  direction={ARROW_ICON_DIRECTIONS.DOWN}
                />
              </span>
            </div>
          </Combobox.Button>

        </div>

        <Combobox.Options className="absolute z-10 mt-1 w-full bg-white text-center shadow-lg">
          {isEmptyArray(filteredItems) ? (
            <div className="py-2 text-muted">No results found</div>
          ) : (
            filteredItems.map((item) => (
              <Combobox.Option
                key={crypto.randomUUID()}
                value={item.value}
                className="cursor-pointer"
              >
                {({ active }) => (
                  <div className={twMerge('py-2 px-3', active && 'bg-input')}>
                    {item.label}
                  </div>
                )}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox>

      {errorMessage && (<ErrorLabel errorMessage={errorMessage} />)}
    </div>
  );
};

function getItemsValue<Item>(items: PlanSettingsSelectItem<Item>[], item: Optional<Item>) {
  return isNullOrUndefined(item) ? undefined : items.find(({ value }) => value === item);
}
